import React, { useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, Heading1, Heading2, Heading3, List, ListOrdered,
  Link as LinkIcon, Code, Quote, Image as ImageIcon, Eye, Edit3, Upload
} from "lucide-react";

// Override <img> rendering inside ReactMarkdown: lazy-load, sane styling.
const MdImg = ({ alt = "", ...props }) => (
  <img alt={alt} {...props} loading="lazy" className="max-w-full h-auto rounded-sm border border-slate-200 my-2" />
);
const MD_COMPONENTS = { img: MdImg };
const MD_PLUGINS = [remarkGfm];

const Btn = ({ icon: Icon, label, onClick, testid, disabled }) => (
  <button type="button" onClick={onClick} title={label} disabled={disabled}
    className="p-1.5 rounded-sm text-kb-ink-muted hover:text-kb-primary hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent" data-testid={testid}>
    <Icon className="w-3.5 h-3.5" />
  </button>
);

/**
 * Compact markdown editor with formatting toolbar.
 * Pass `courseId` to enable the "Upload image" toolbar button — uploads route
 * to `/courses/{courseId}/assets` and the public file URL is inserted into the
 * markdown as `![alt](url)`.
 */
export default function MarkdownEditor({
  value,
  onChange,
  placeholder = "Write in Markdown — use the toolbar if you don't know the syntax",
  rows = 8,
  testId = "markdown-editor",
  courseId = null,
}) {
  const textareaRef = useRef(null);
  const uploadRef = useRef(null);
  const [preview, setPreview] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);

  const wrap = (before, after = before, fallback = "text") => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const v = ta.value;
    const selected = v.slice(start, end) || fallback;
    const next = v.slice(0, start) + before + selected + after + v.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, start + before.length + selected.length);
    });
  };

  const insertAtCursor = (text) => {
    const ta = textareaRef.current;
    if (!ta) { onChange((value || "") + text); return; }
    const start = ta.selectionStart, end = ta.selectionEnd;
    const v = ta.value;
    onChange(v.slice(0, start) + text + v.slice(end));
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const prefixLines = (prefix, fallback = "text") => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const v = ta.value;
    const selStart = v.lastIndexOf("\n", start - 1) + 1;
    const selEnd = end;
    const block = v.slice(selStart, selEnd) || fallback;
    const lines = block.split("\n").map((l, i) => {
      if (prefix === "1. ") return `${i + 1}. ${l}`;
      return prefix + l;
    });
    const replaced = lines.join("\n");
    onChange(v.slice(0, selStart) + replaced + v.slice(selEnd));
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(selStart, selStart + replaced.length);
    });
  };

  const insertLink = () => {
    const url = window.prompt("Link URL");
    if (!url) return;
    wrap(`[`, `](${url})`, "link text");
  };

  const insertImageUrl = () => {
    const url = window.prompt("Image URL (or use Upload to attach a file)");
    if (!url) return;
    wrap(`![`, `](${url})`, "alt text");
  };

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!courseId) {
      toast.error("Save the course first, then upload images.");
      return;
    }
    if (!f.type.startsWith("image/")) {
      toast.error("Only image files supported here");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5MB)");
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      const b64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
      const r = await api.post(`/courses/${courseId}/assets`, {
        name: f.name, mime: f.type, size_kb: Math.round(f.size / 1024),
        content_base64: b64, kind: "image",
      });
      const url = `${BACKEND_URL}/api/courses/${courseId}/assets/${r.data.id}/file`;
      const alt = (f.name.split(".")[0] || "image").slice(0, 40);
      insertAtCursor(`\n\n![${alt}](${url})\n\n`);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const Btn2 = Btn; // alias kept for backwards consistency
  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden bg-white" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 px-2 py-1.5 bg-slate-50">
        <Btn2 icon={Bold} label="Bold (**text**)" onClick={() => wrap("**", "**", "bold")} testid="md-bold" />
        <Btn2 icon={Italic} label="Italic (*text*)" onClick={() => wrap("*", "*", "italic")} testid="md-italic" />
        <span className="w-px h-4 bg-slate-300 mx-1" />
        <Btn2 icon={Heading1} label="Heading 1" onClick={() => prefixLines("# ", "Heading")} testid="md-h1" />
        <Btn2 icon={Heading2} label="Heading 2" onClick={() => prefixLines("## ", "Heading")} testid="md-h2" />
        <Btn2 icon={Heading3} label="Heading 3" onClick={() => prefixLines("### ", "Heading")} testid="md-h3" />
        <span className="w-px h-4 bg-slate-300 mx-1" />
        <Btn2 icon={List} label="Bullet list" onClick={() => prefixLines("- ", "item")} testid="md-ul" />
        <Btn2 icon={ListOrdered} label="Numbered list" onClick={() => prefixLines("1. ", "item")} testid="md-ol" />
        <Btn2 icon={Quote} label="Quote" onClick={() => prefixLines("> ", "quote")} testid="md-quote" />
        <Btn2 icon={Code} label="Inline code" onClick={() => wrap("`", "`", "code")} testid="md-code" />
        <span className="w-px h-4 bg-slate-300 mx-1" />
        <Btn2 icon={LinkIcon} label="Insert link" onClick={insertLink} testid="md-link" />
        <Btn2 icon={ImageIcon} label="Insert image by URL" onClick={insertImageUrl} testid="md-image" />
        <Btn2
          icon={Upload}
          label={courseId ? "Upload image" : "Save course first to enable upload"}
          onClick={() => uploadRef.current?.click()}
          testid="md-image-upload"
          disabled={uploading || !courseId}
        />
        <input ref={uploadRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} />
        <span className="flex-1" />
        <button type="button" onClick={() => setPreview(!preview)}
          className="kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm text-kb-primary hover:bg-kb-primary/5 inline-flex items-center gap-1.5"
          data-testid="md-preview-toggle">
          {preview ? (<><Edit3 className="w-3 h-3" /> Edit</>) : (<><Eye className="w-3 h-3" /> Preview</>)}
        </button>
      </div>
      {preview ? (
        <div className="prose prose-slate max-w-none p-3 text-sm prose-headings:font-serif min-h-[160px]">
          <ReactMarkdown remarkPlugins={MD_PLUGINS} components={MD_COMPONENTS}>{value || "_Nothing to preview_"}</ReactMarkdown>
        </div>
      ) : (
        <textarea ref={textareaRef} rows={rows}
          className="w-full p-3 text-sm font-mono resize-y border-0 focus:outline-none focus:ring-0"
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testId}-textarea`} />
      )}
    </div>
  );
}
